// /login/recovery-codes — show the 10 recovery codes ONCE. User must download
// or copy + acknowledge. After confirmation, navigate to /login/mfa-verify.

import { LoginShell } from '@/components/LoginShell';
import { useOnceMountGuard } from '@/lib/auth-guards';
import { Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/login/recovery-codes')({
  component: RecoveryCodesPage,
});

function RecoveryCodesPage() {
  const codes = useAuth((s) => s.pendingRecoveryCodes);
  const setRecoveryCodes = useAuth((s) => s.setRecoveryCodes);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  useOnceMountGuard(() => useAuth.getState().pendingRecoveryCodes != null, {
    to: '/login/mfa-verify',
  });

  if (!codes) return null;

  const text = `Eidollon recovery codes — generated ${new Date().toISOString().slice(0, 10)}\n\n${codes.map((c, i) => `${String(i + 1).padStart(2, ' ')}. ${c}`).join('\n')}\n\nEach code can be used ONCE if you lose your authenticator app.`;

  function download() {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eidollon-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function continueOn() {
    setRecoveryCodes(null);
    void navigate({ to: '/login/mfa-verify' });
  }

  return (
    <LoginShell
      step="STEP 3 / 4 · RECOVERY CODES"
      title="Save your recovery codes"
      sub="Each code is single-use. They can substitute for a TOTP code if you lose your authenticator. We will not show them again."
    >
      <div
        style={{
          padding: 12,
          background: 'var(--bg-pane-2)',
          border: '1px solid var(--bd)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          fontFamily: 'var(--mono)',
          fontSize: 12,
        }}
      >
        {codes.map((c, i) => (
          <div
            key={c}
            style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 4px' }}
          >
            <span className="dim2">{String(i + 1).padStart(2, ' ')}.</span>
            <span>{c}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          className="btn"
          onClick={download}
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
        >
          DOWNLOAD .TXT
        </button>
        <button
          className="btn"
          onClick={copy}
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {copied ? (
            <Pill tone="green" dot={false}>
              COPIED
            </Pill>
          ) : (
            'COPY ALL'
          )}
        </button>
      </div>
      <button
        className="btn pri"
        type="button"
        onClick={continueOn}
        style={{ height: 32, justifyContent: 'center' }}
      >
        I'VE SAVED THEM · CONTINUE
      </button>
    </LoginShell>
  );
}
